import { Injectable, inject } from '@angular/core';
import { httpsCallable, Functions, HttpsCallableResult } from '@angular/fire/functions';
import {
  Firestore, collection, query, orderBy, onSnapshot,
} from '@angular/fire/firestore';
import { from, map, Observable } from 'rxjs';
import { Family } from '../models/family.model';
import { DataService } from './data';

/** Tipos base */
export type FamilyRole = 'PADRE' | 'MADRE' | 'HIJO';

export interface MoveItem {
  memberId: string;
  role?: FamilyRole;   // en algunos casos no es obligatorio (p.ej. REMOVE/TRANSFER)
}

/** Respuestas frecuentes del backend (ajusta según tu CF) */
export interface CreateFamilyResult {
  familyId: string;
  message?: string;
}

export interface BulkResult {
  updatedMembers?: string[]; // p.ej. retornado por BULK_REMOVE / BULK_MOVE_TO_FAMILY
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class FamiliesService {
  private functions = inject(Functions);
  private firestore = inject(Firestore);
  private dataService = inject(DataService);
  private familiesUnsub: (() => void) | null = null;

  /** Helper para evitar repetir httpsCallable y mantener tipos */
  private callCloudFn<R = any>(payload: any): Observable<R> {
    // RequestData = any, ResponseData = R
    const fn = httpsCallable<any, R>(this.functions, 'gestionarFamiliaV2');
    return from(fn(payload)).pipe(
      map((res: HttpsCallableResult<R>) => res.data as R)
    );
  }

  /** Salud */
  ping() {
    return this.callCloudFn({ action: 'PING', churchId: 'TEST' });
  }

  /** Crear familia (opcionalmente sugiriendo nombre a partir de un memberId) */
  createFamily(
    churchId: string,
    opts?: { nombre?: string; memberIdForName?: string; dryRun?: boolean }
  ) {
    return this.callCloudFn<CreateFamilyResult>({
      action: 'CREATE_FAMILY',
      churchId,
      nombreFamiliaPreferido: opts?.nombre ?? null,
      memberId: opts?.memberIdForName ?? null,
      dryRun: !!opts?.dryRun,
    });
  }

  /** Crear nueva familia y mover miembro base + (opcional) cónyuge */
  moveMemberNewFamily(
    churchId: string,
    memberId: string,
    spouseId?: string,
    nombre?: string,
    dryRun = false
  ) {
    return this.callCloudFn<CreateFamilyResult & BulkResult>({
      action: 'MOVE_MEMBER_NEW_FAMILY',
      churchId,
      memberId,
      spouseId: spouseId ?? null,
      nombreFamiliaPreferido: nombre ?? null,
      dryRun
    });
  }

  /** Mover un miembro a una familia con rol asignado */
  moveMemberToFamily(
    churchId: string,
    familyId: string,
    memberId: string,
    role: FamilyRole,
    dryRun = false
  ) {
    return this.callCloudFn<BulkResult>({
      action: 'MOVE_MEMBER_TO_FAMILY',
      churchId,
      familyId,
      memberId,
      role,
      dryRun
    });
  }

  /** Agregar hijo a una familia */
  addChild(
    churchId: string,
    familyId: string,
    childId: string,
    dryRun = false
  ) {
    return this.callCloudFn<BulkResult>({
      action: 'ADD_CHILD',
      churchId,
      familyId,
      childId,
      dryRun
    });
  }

  /** Remover un miembro de su familia (cierra membresía / familyId = null) */
  removeMember(
    churchId: string,
    memberId: string,
    dryRun = false
  ) {
    return this.callCloudFn<BulkResult>({
      action: 'REMOVE_MEMBER',
      churchId,
      memberId,
      dryRun
    });
  }

  /** Vincular cónyuge (y asignar roles si quieres fijarlos explícitamente) */
  linkSpouse(
    churchId: string,
    memberId: string,
    spouseId: string,
    opts?: { role?: FamilyRole; spouseRole?: FamilyRole; nombre?: string; dryRun?: boolean }
  ) {
    return this.callCloudFn<BulkResult>({
      action: 'LINK_SPOUSE',
      churchId,
      memberId,
      spouseId,
      role: opts?.role ?? null,
      spouseRole: opts?.spouseRole ?? null,
      nombreFamiliaPreferido: opts?.nombre ?? null,
      dryRun: !!opts?.dryRun
    });
  }

  /** Mover varios miembros a una familia (cada item puede llevar rol) */
  bulkMoveToFamily(
    churchId: string,
    familyId: string,
    items: Array<MoveItem>,
    dryRun = false
  ) {
    return this.callCloudFn<BulkResult>({
      action: 'BULK_MOVE_TO_FAMILY',
      churchId,
      familyId,
      items,
      dryRun
    });
  }

  /** Desvincular cónyuge */
  unlinkSpouse(
    churchId: string,
    memberId: string,
    dryRun = false
  ) {
    return this.callCloudFn<BulkResult>({
      action: 'UNLINK_SPOUSE',
      churchId,
      memberId,
      dryRun
    });
  }

  /** Remover varios miembros (manteniendo tu shape de items) */
  bulkRemove(
    churchId: string,
    memberIds: string[],
    dryRun = false
  ) {
    return this.callCloudFn<BulkResult>({
      action: 'BULK_REMOVE',
      churchId,
      items: memberIds.map(id => ({ memberId: id })),
      dryRun
    });
  }

  /** Transferir hijos de una familia a otra */
  transferChildren(
    churchId: string,
    sourceFamilyId: string,
    targetFamilyId: string,
    childIds: string[],
    dryRun = false
  ) {
    return this.callCloudFn<BulkResult>({
      action: 'TRANSFER_CHILDREN',
      churchId,
      sourceFamilyId,
      targetFamilyId,
      items: childIds.map(id => ({ memberId: id })),
      dryRun
    });
  }

  /** Desactivar familia (force si hay miembros aún) */
  deactivateFamily(
    churchId: string,
    familyId: string,
    force = false,
    dryRun = false
  ) {
    return this.callCloudFn<BulkResult>({
      action: 'DEACTIVATE_FAMILY',
      churchId,
      familyId,
      force,
      dryRun
    });
  }

  stopListening(): void {
    if (this.familiesUnsub) {
      this.familiesUnsub();
      this.familiesUnsub = null;
    }
  }

  /** Suscripción en vivo a familias */
  loadAllFamiliesForChurch(churchId: string): Observable<Family[]> {
    this.stopListening();

    const familiesCol = collection(this.firestore, `iglesias/${churchId}/familias`);
    // Temporalmente sin orderBy para evitar problemas con índices o campos faltantes
    const q = query(familiesCol);

    return new Observable<Family[]>(observer => {
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const families = snapshot.docs.map(doc => ({
          familyId: doc.id,
          ...(doc.data() as any)
        })) as Family[];

        this.dataService.updateFamilies(families);
        this.dataService.setFamiliesLoaded(true);

        observer.next(families);
      }, (error) => {
        observer.error(error);
      });

      this.familiesUnsub = unsubscribe;
      return () => {
        unsubscribe();
        if (this.familiesUnsub === unsubscribe) {
          this.familiesUnsub = null;
        }
      };
    });
  }
}
